// src/PauseMenu.js
import { useState, useEffect, useCallback, useMemo } from 'react';
import './PauseMenu.css';
import PuzzleLLMPanel from './PuzzleLLMPanel';
import StatsPlot from './StatsPlot';

const CONTROLS = [
    { action: 'Move Cursor', keys: 'W A S D / Arrows' },
    { action: 'Place Man', keys: 'P' },
    { action: 'Start Jump', keys: 'J' },
    { action: 'Confirm Move', keys: 'Enter' },
    { action: 'Undo Step', keys: 'Backspace' },
    { action: 'Cancel / Menu', keys: 'Escape' },
    { action: null }, // spacer
    { action: 'Focus Panel', keys: 'Shift + A/D or Arrows' },
    { action: 'History Navigation', keys: 'Up / Down' },
    { action: 'Toggle / Select', keys: 'Enter' },
    { action: 'Branch from History', keys: 'B' },
    { action: 'Return to Live', keys: 'L' },
    { action: 'Toggle Coordinates', keys: 'C' },
];

// Mini dial control reused inside the puzzle-config disclosure. Mirrors
// the GameOverModal's Dial; kept inline to avoid a circular import.
const Dial = ({ label, value, onPrev, onNext, min, max }) => (
    <div className="puzzle-dial">
        <button type="button" className="puzzle-dial__btn"
            onClick={onPrev} disabled={value <= min} aria-label={`Decrease ${label}`}>‹</button>
        <span className="puzzle-dial__label">
            {label}: <strong>{value}</strong>
            <span className="puzzle-dial__range">{min}–{max}</span>
        </span>
        <button type="button" className="puzzle-dial__btn"
            onClick={onNext} disabled={value >= max} aria-label={`Increase ${label}`}>›</button>
    </div>
);

const PUZZLE_TYPE_OPTIONS = [
    { id: 'win',         label: 'Win',         min: 1, max: 8 },
    { id: 'threat',      label: 'Threat',      min: 2, max: 6 },
    { id: 'passthrough', label: 'Passthrough', min: 2, max: 8 },
    { id: 'denial',      label: 'Denial',      min: 4, max: 9 },
];

const PauseMenu = ({
    isOpen,
    onClose,
    onResume,
    onNewGame,
    onResign,
    onMainMenu,
    historyLength = 0,
    treeNodeCount = 0,
    stats = null,
    puzzleMode = false,
    puzzleControls = null,
    onPuzzleControlsChange = null,
    puzzleEvalResult = null,
    // Watch-an-LLM puzzles only (null when the user solves by hand).
    puzzleLLM = null,
}) => {
    const [confirmingAction, setConfirmingAction] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(0);
    // 'puzzle' is the new-puzzle-config disclosure; only relevant in puzzleMode.
    const [expandedSections, setExpandedSections] = useState({ controls: false, stats: false, puzzle: false, model: true });

    const confirmLabels = { reset: 'Reset', resign: 'Resign', exit: 'Exit' };
    const newGameLabel = puzzleMode ? 'New Puzzle' : 'New Game';
    const menuItems = useMemo(() => {
        if (confirmingAction) {
            return [
                { label: 'Yes, ' + (confirmLabels[confirmingAction] || 'Confirm'), action: 'confirm', variant: 'danger' },
                { label: 'Cancel', action: 'cancel' },
            ];
        }
        const items = [{ label: 'Resume', action: 'resume' }];
        if (onNewGame) items.push({ label: newGameLabel, action: 'reset' });
        // Puzzles are single-turn; resigning makes no sense.
        if (onResign && !puzzleMode) items.push({ label: 'Resign', action: 'resign', variant: 'danger' });
        items.push({ label: 'Main Menu', action: 'main_menu' });
        return items;
    }, [confirmingAction, onNewGame, onResign, puzzleMode, newGameLabel]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleAction = useCallback((action) => {
        if (action === 'resume') {
            onResume?.();
            onClose?.();
        } else if (action === 'reset') {
            // Puzzle mode: skip the confirmation prompt — every puzzle is
            // single-turn and "regenerate" is the expected interaction.
            if (puzzleMode) {
                onNewGame?.();
                onClose?.();
                return;
            }
            setConfirmingAction('reset');
            setSelectedIndex(1);
        } else if (action === 'resign') {
            setConfirmingAction('resign');
            setSelectedIndex(1);
        } else if (action === 'main_menu') {
            setConfirmingAction('exit');
            setSelectedIndex(1);
        } else if (action === 'confirm') {
            if (confirmingAction === 'reset') {
                onNewGame?.();
            } else if (confirmingAction === 'resign') {
                onResign?.();
            } else if (confirmingAction === 'exit') {
                onMainMenu?.();
            }
            setConfirmingAction(null);
            onClose?.();
        } else if (action === 'cancel') {
            setConfirmingAction(null);
            setSelectedIndex(0);
        }
    }, [confirmingAction, onResume, onNewGame, onResign, onMainMenu, onClose, puzzleMode]);

    const toggleSection = useCallback((section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    }, []);

    useEffect(() => {
        if (!isOpen) {
            setConfirmingAction(null);
            setSelectedIndex(0);
            setExpandedSections({ controls: false, stats: false, puzzle: false, model: true });
            return;
        }

        const handleKeyDown = (e) => {
            // Stop propagation to prevent App.js from also handling these keys
            e.stopPropagation();

            if (e.key === 'Escape') {
                if (confirmingAction) {
                    setConfirmingAction(null);
                    setSelectedIndex(0);
                } else {
                    onClose?.();
                }
                e.preventDefault();
            } else if (e.key === 'ArrowUp' || e.key === 'w' || e.key === 'W' || e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') {
                setSelectedIndex(i => Math.max(0, i - 1));
                e.preventDefault();
            } else if (e.key === 'ArrowDown' || e.key === 's' || e.key === 'S' || e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') {
                setSelectedIndex(i => Math.min(menuItems.length - 1, i + 1));
                e.preventDefault();
            } else if (e.key === 'Enter') {
                handleAction(menuItems[selectedIndex].action);
                e.preventDefault();
            }
        };

        // Use capture phase to intercept before App.js
        window.addEventListener('keydown', handleKeyDown, true);
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [isOpen, confirmingAction, menuItems, selectedIndex, handleAction, onClose]);

    if (!isOpen) return null;

    const totalTurns = Math.max(0, historyLength - 1); // Linear history = turns
    const totalMoves = Math.max(0, treeNodeCount - 1); // Tree nodes = moves

    // Extract stats if provided
    const p1Stats = stats?.p1 || {};
    const p2Stats = stats?.p2 || {};
    const possession = stats?.possession || null;
    const pieceTilt = stats?.pieceTilt || null;
    const statsHistory = stats?.statsHistory || [];

    const hasJumpStats = (p1Stats.avgJumpsPerSeq !== undefined || p2Stats.avgJumpsPerSeq !== undefined);
    const hasPossession = possession !== null;
    const hasPieceTilt = pieceTilt !== null;
    const hasStatsHistory = statsHistory.length >= 2;

    return (
        <div className="pause-overlay" onClick={() => !confirmingAction && onClose?.()} onTouchEnd={(e) => { if (e.target === e.currentTarget && !confirmingAction) { e.preventDefault(); onClose?.(); } }}>
            <div className="pause-modal" onClick={e => e.stopPropagation()} onTouchEnd={e => e.stopPropagation()}>
                <div className="pause-header">
                    <h2 className="pause-title">{puzzleMode ? 'PUZZLE MENU' : 'GAME MENU'}</h2>
                </div>

                {/* Persistent puzzle verdict — shown after the puzzle has been
                    judged so the user can re-open the pause menu and see the
                    same pass/fail label without retriggering the modal. */}
                {puzzleMode && puzzleEvalResult && (
                    <div className={`pause-puzzle-verdict ${puzzleEvalResult.solved ? 'solved' : 'failed'}`}>
                        <span className="pause-puzzle-verdict__status">
                            {puzzleEvalResult.solved ? 'SOLVED' : 'FAILED'}
                        </span>
                        {puzzleEvalResult.label && (
                            <span className="pause-puzzle-verdict__label">{puzzleEvalResult.label}</span>
                        )}
                    </div>
                )}

                <div className="pause-content">
                    {/* Controls Section */}
                    <div className="pause-expandable-section">
                        <button
                            className="pause-section-header"
                            onClick={(e) => { toggleSection('controls'); e.currentTarget.blur(); }}
                        >
                            <span className="pause-section-title">CONTROLS</span>
                            <span className={`pause-section-toggle ${expandedSections.controls ? 'expanded' : ''}`}>
                                {expandedSections.controls ? '▾' : '▸'}
                            </span>
                        </button>
                        {expandedSections.controls && (
                            <div className="pause-section-content">
                                <div className="controls-grid">
                                    {CONTROLS.map((ctrl, i) => (
                                        ctrl.action === null ? (
                                            <div key={i} className="control-spacer" />
                                        ) : (
                                            <div key={i} className="control-row">
                                                <span className="control-action">{ctrl.action}</span>
                                                <span className="control-keys">{ctrl.keys}</span>
                                            </div>
                                        )
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Model section: only when the puzzle was entered through
                        Watch an LLM. Shows who plays the next attempt and
                        lets the user switch the model here too. */}
                    {puzzleMode && puzzleLLM && (
                        <div className="pause-expandable-section">
                            <button
                                className="pause-section-header"
                                onClick={(e) => { toggleSection('model'); e.currentTarget.blur(); }}
                            >
                                <span className="pause-section-title">MODEL: {puzzleLLM.model}</span>
                                <span className={`pause-section-toggle ${expandedSections.model ? 'expanded' : ''}`}>
                                    {expandedSections.model ? '\u25BE' : '\u25B8'}
                                </span>
                            </button>
                            {expandedSections.model && (
                                <div className="pause-section-content">
                                    <PuzzleLLMPanel puzzleLLM={puzzleLLM} showStats={!!puzzleLLM.stats} />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Puzzle Config (puzzle mode only). Tweaks here apply
                        to the next 'New Puzzle' click; otherwise the next
                        regen reuses current values with a fresh seed. */}
                    {puzzleMode && puzzleControls && onPuzzleControlsChange && (() => {
                        const setType = (id) => {
                            const meta = PUZZLE_TYPE_OPTIONS.find(t => t.id === id);
                            const clamped = Math.max(meta?.min ?? 1, Math.min(meta?.max ?? 4, puzzleControls.num_jumps));
                            onPuzzleControlsChange({
                                ...puzzleControls,
                                puzzle_type: id,
                                num_jumps: clamped,
                            });
                        };
                        const update = (k, delta, mn, mx) => {
                            const next = Math.max(mn, Math.min(mx, (puzzleControls[k] ?? 0) + delta));
                            onPuzzleControlsChange({ ...puzzleControls, [k]: next });
                        };
                        const typeMeta = PUZZLE_TYPE_OPTIONS.find(t => t.id === puzzleControls.puzzle_type) || PUZZLE_TYPE_OPTIONS[0];
                        const isThreat = puzzleControls.puzzle_type === 'threat';
                        const minJumps = typeMeta.min;
                        const maxJumps = typeMeta.max;
                        const displayedPlayer = isThreat ? (3 - puzzleControls.player) : puzzleControls.player;
                        const setDisplayedPlayer = (p) => onPuzzleControlsChange({
                            ...puzzleControls,
                            player: isThreat ? (3 - p) : p,
                        });
                        return (
                            <div className="pause-expandable-section">
                                <button
                                    className="pause-section-header"
                                    onClick={(e) => { toggleSection('puzzle'); e.currentTarget.blur(); }}
                                >
                                    <span className="pause-section-title">NEW PUZZLE CONFIG</span>
                                    <span className={`pause-section-toggle ${expandedSections.puzzle ? 'expanded' : ''}`}>
                                        {expandedSections.puzzle ? '▾' : '▸'}
                                    </span>
                                </button>
                                {expandedSections.puzzle && (
                                    <div className="pause-section-content">
                                        <div className="puzzle-controls" style={{ background: 'transparent', padding: 0 }}>
                                            <div className="puzzle-controls__type">
                                                {PUZZLE_TYPE_OPTIONS.map(t => (
                                                    <button
                                                        key={t.id}
                                                        type="button"
                                                        className={`puzzle-dial__btn ${puzzleControls.puzzle_type === t.id ? 'active' : ''}`}
                                                        onClick={() => setType(t.id)}
                                                    >{t.label}</button>
                                                ))}
                                            </div>
                                            <Dial
                                                label={
                                                    puzzleControls.puzzle_type === 'threat'      ? 'Threat depth (jumps)'
                                                    : puzzleControls.puzzle_type === 'denial'    ? 'Max total jumps'
                                                    : puzzleControls.puzzle_type === 'passthrough' ? 'Jumps in sequence'
                                                    : 'Jumps to win'
                                                }
                                                value={Math.max(minJumps, Math.min(maxJumps, puzzleControls.num_jumps))}
                                                onPrev={() => update('num_jumps', -1, minJumps, maxJumps)}
                                                onNext={() => update('num_jumps', +1, minJumps, maxJumps)}
                                                min={minJumps}
                                                max={maxJumps}
                                            />
                                            {puzzleControls.puzzle_type === 'win' && (
                                                <Dial
                                                    label="Max noise stones"
                                                    value={puzzleControls.noise}
                                                    onPrev={() => update('noise', -1, 0, 20)}
                                                    onNext={() => update('noise', +1, 0, 20)}
                                                    min={0}
                                                    max={20}
                                                />
                                            )}
                                            <Dial
                                                label="Max stones per jump"
                                                value={puzzleControls.max_jump_len}
                                                onPrev={() => update('max_jump_len', -1, 1, 8)}
                                                onNext={() => update('max_jump_len', +1, 1, 8)}
                                                min={1}
                                                max={8}
                                            />
                                            <div className="puzzle-controls__role">
                                                {isThreat ? 'Player facing threat' : 'Player to move'}
                                            </div>
                                            <div className="puzzle-controls__player">
                                                <button
                                                    type="button"
                                                    className={`puzzle-dial__btn ${displayedPlayer === 1 ? 'active' : ''}`}
                                                    onClick={() => setDisplayedPlayer(1)}
                                                >P1 (top)</button>
                                                <button
                                                    type="button"
                                                    className={`puzzle-dial__btn ${displayedPlayer === 2 ? 'active' : ''}`}
                                                    onClick={() => setDisplayedPlayer(2)}
                                                >P2 (bottom)</button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {/* Stats Section */}
                    <div className="pause-expandable-section">
                        <button
                            className="pause-section-header"
                            onClick={(e) => { toggleSection('stats'); e.currentTarget.blur(); }}
                        >
                            <span className="pause-section-title">STATS</span>
                            <span className={`pause-section-toggle ${expandedSections.stats ? 'expanded' : ''}`}>
                                {expandedSections.stats ? '▾' : '▸'}
                            </span>
                        </button>
                        {expandedSections.stats && (
                            <div className="pause-section-content">
                                {/* Live stats section */}
                                <h4 className="pause-subsection-title">CURRENT</h4>
                                <div className="pause-stats-grid">
                                    <div className="pause-stat-item">
                                        <span className="pause-stat-value">{totalTurns}</span>
                                        <span className="pause-stat-label">turns</span>
                                    </div>
                                    <div className="pause-stat-item">
                                        <span className="pause-stat-value">{totalMoves}</span>
                                        <span className="pause-stat-label">moves</span>
                                    </div>
                                </div>

                                {hasJumpStats && (
                                    <div className="pause-stats-comparison">
                                        <div className="pause-stats-row pause-stats-header">
                                            <span className="pause-stats-label"></span>
                                            <span className="pause-stats-p1">P1</span>
                                            <span className="pause-stats-p2">P2</span>
                                        </div>
                                        <div className="pause-stats-row">
                                            <span className="pause-stats-label">Jump sequences</span>
                                            <span className="pause-stats-p1">{p1Stats.jumpSequenceCount ?? '-'}</span>
                                            <span className="pause-stats-p2">{p2Stats.jumpSequenceCount ?? '-'}</span>
                                        </div>
                                        <div className="pause-stats-row">
                                            <span className="pause-stats-label">Avg jumps/seq</span>
                                            <span className="pause-stats-p1">{p1Stats.avgJumpsPerSeq?.toFixed(1) ?? '-'}</span>
                                            <span className="pause-stats-p2">{p2Stats.avgJumpsPerSeq?.toFixed(1) ?? '-'}</span>
                                        </div>
                                        <div className="pause-stats-row">
                                            <span className="pause-stats-label">Avg pieces/jump</span>
                                            <span className="pause-stats-p1">{p1Stats.avgPiecesPerJump?.toFixed(1) ?? '-'}</span>
                                            <span className="pause-stats-p2">{p2Stats.avgPiecesPerJump?.toFixed(1) ?? '-'}</span>
                                        </div>
                                    </div>
                                )}

                                {(hasPossession || hasPieceTilt || hasStatsHistory) && (
                                    <div className="pause-detailed-stats">
                                        {/* Stats plot showing field position and piece tilt over time */}
                                        {hasStatsHistory && (
                                            <>
                                                <h4 className="pause-subsection-title">OVER TIME</h4>
                                                <StatsPlot statsHistory={statsHistory} />
                                            </>
                                        )}

                                        {/* Fallback bars when no time-series data available */}
                                        {!hasStatsHistory && hasPossession && (
                                            <div className="pause-possession-stats">
                                                <h4 className="pause-subsection-title">FIELD POSITION</h4>
                                                <div className="pause-possession-bar-container">
                                                    <div
                                                        className="pause-possession-bar p1"
                                                        style={{ width: `${possession.p1}%` }}
                                                    />
                                                    <div
                                                        className="pause-possession-bar p2"
                                                        style={{ width: `${possession.p2}%` }}
                                                    />
                                                </div>
                                                <div className="pause-possession-labels">
                                                    <span className="pause-possession-p1">{possession.p1}% P1</span>
                                                    <span className="pause-possession-p2">P2 {possession.p2}%</span>
                                                </div>
                                            </div>
                                        )}

                                        {!hasStatsHistory && hasPieceTilt && (
                                            <div className="pause-possession-stats">
                                                <h4 className="pause-subsection-title">PIECE TILT</h4>
                                                <div className="pause-possession-bar-container">
                                                    <div
                                                        className="pause-possession-bar p1"
                                                        style={{ width: `${pieceTilt.p1}%` }}
                                                    />
                                                    <div
                                                        className="pause-possession-bar p2"
                                                        style={{ width: `${pieceTilt.p2}%` }}
                                                    />
                                                </div>
                                                <div className="pause-possession-labels">
                                                    <span className="pause-possession-p1">{pieceTilt.p1}% P1</span>
                                                    <span className="pause-possession-p2">P2 {pieceTilt.p2}%</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {!hasJumpStats && !hasPossession && !hasPieceTilt && !hasStatsHistory && (
                                    <p className="pause-no-stats">No jump sequences yet</p>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="menu-section">
                        {confirmingAction && (
                            <p className="confirm-prompt">
                                {confirmingAction === 'reset'
                                    ? 'Start a new game? Current progress will be lost.'
                                    : confirmingAction === 'resign'
                                    ? 'Resign this game? This counts as a loss.'
                                    : 'Return to main menu? Current progress will be lost.'}
                            </p>
                        )}
                        <div className="pause-menu-buttons">
                            {menuItems.map((item, i) => (
                                <button
                                    key={item.action}
                                    className={`pause-menu-btn ${item.variant ?? ''} ${i === selectedIndex ? 'selected' : ''}`}
                                    onClick={(e) => { handleAction(item.action); e.currentTarget.blur(); }}
                                    onMouseEnter={() => setSelectedIndex(i)}
                                >
                                    <span className="pause-menu-btn__sweep" />
                                    <span className="pause-menu-btn__text">{item.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PauseMenu;
